/**
 * **전송 오류 → 사용자 문장 — 정본 하나**(D-87).
 *
 * 서비스 경계(BFF 경유 Connect 클라이언트)가 요청을 끝내지 못하면 connect-web 은 `ConnectError` 를 던지고, 그 `message` 는
 * «[unknown] Failed to fetch» 같은 원문이다. 그 원문을 화면에 두지 않는다 — 경계의 클라이언트를 {@link createTranslatingClient} 로
 * 지으면 실패는 {@link ServiceCallError}(문장 + 코드·원문)로 바뀌어 나온다. 소비자(시작 실패 창·배너·토스트)는 `message` 를
 * 보일 뿐 코드를 다시 번역하지 않는다.
 *
 * 번역은 전송(인터셉터)이 아니라 클라이언트에 둔다 — connect 의 호출 실행은 인터셉터가 던진 오류를 `ConnectError.from` 으로 다시
 * 감싸므로(«[unknown] <문장>») 전송 안에서 바꾼 오류는 클라이언트 밖으로 그대로 나오지 못한다.
 *
 * 문장은 코드마다 한 벌이고 서비스가 말하는 법({@link ServiceVoice} — 서비스 이름, 요청마다의 동작 이름)을 인자로 받는다.
 * 같은 코드가 그 서비스에서 따로 띠는 뜻(inputd 의 NotFound = 재시작 파일 없음 등)은 그 서비스의 선언(`meanings`)이 말한다.
 * 원문(서버의 detail)은 `ServiceCallError.detail` 에 남는다 — 화면이 아니라 console 몫이다.
 */
import { Code, ConnectError, createPromiseClient } from '@connectrpc/connect';
import type { PromiseClient, Transport } from '@connectrpc/connect';
import { MethodKind, type ServiceType } from '@bufbuild/protobuf';

/** 문장에 들어가는 재료 — 서비스 이름·동작 이름·서버 detail 의 첫 줄과 전문 */
export interface TransportErrorFacts {
  readonly service: string;
  readonly action: string;
  readonly firstLine: string;
  readonly detail: string;
}

type Sentence = (facts: TransportErrorFacts) => string;

/** 서비스가 말하는 법 — 선언은 그 서비스의 클라이언트 곁에 한 번 둔다 */
export interface ServiceVoice<S extends ServiceType = ServiceType> {
  /** 무엇에 닿지 못했나 — 예: «입력 검증 서비스». 문장에서 «…에»·«…의» 로만 쓰인다(받침과 무관한 조사) */
  readonly service: string;
  /** 요청(서비스의 메서드)마다 무엇을 하던 중이었나 — 예: «입력 검증». 문장에서 «… 요청»·«… 권한» 으로 쓰인다.
   *  서비스의 메서드를 모두 적어야 한다(빠진 메서드는 타입이 막는다) */
  readonly actions: { readonly [K in keyof S['methods']]: string };
  /** 이 서비스에서 코드가 따로 띠는 뜻 — 공통 문장보다 앞선다 */
  readonly meanings?: Readonly<Partial<Record<Code, Sentence>>>;
}

/** 문장 뒤에 서버 detail 의 첫 줄을 붙인다(비면 붙이지 않는다) — 서비스의 `meanings` 도 쓴다 */
export const withFirstLine = (text: string, firstLine: string): string => (firstLine ? `${text}: ${firstLine}` : text);

/** 코드 → 공통 문장. 여기 없는 코드는 {@link otherSentence} */
const COMMON_SENTENCES: Readonly<Partial<Record<Code, Sentence>>> = {
  [Code.Unavailable]: ({ service }) => `${service}의 응답이 없습니다(혼잡하거나 준비 중) — 잠시 뒤 다시 시도하세요`,
  [Code.DeadlineExceeded]: ({ action }) => `${action} 요청이 시간 안에 끝나지 않았습니다 — 다시 시도하세요`,
  [Code.Unauthenticated]: ({ action }) => `로그인 정보가 없거나 만료되어 ${action} 요청을 보내지 못했습니다 — 다시 로그인하세요`,
  [Code.PermissionDenied]: ({ action, firstLine }) => withFirstLine(`${action} 권한이 없습니다`, firstLine),
  [Code.InvalidArgument]: ({ action, firstLine }) => withFirstLine(`${action} 요청이 거절되었습니다`, firstLine),
  [Code.NotFound]: ({ action, firstLine }) => withFirstLine(`${action} 대상을 찾지 못했습니다`, firstLine),
  [Code.FailedPrecondition]: ({ action, firstLine }) =>
    `${withFirstLine(`이 배포에서는 ${action} 요청을 받을 수 없습니다`, firstLine)} — 관리자에게 문의하세요`,
  [Code.Internal]: ({ service, action, firstLine }) => withFirstLine(`${service}의 ${action} 처리가 비정상 종료했습니다`, firstLine),
};

const otherSentence = (code: Code, { service, firstLine }: TransportErrorFacts): string =>
  withFirstLine(`${service} 오류 (${Code[code] ?? code})`, firstLine);

/** 브라우저가 서버에 닿지 못했다(fetch 의 TypeError — «Failed to fetch») — connect-web 은 이것을 Unknown 으로 감싼다 */
export function isUnreachable(err: unknown): boolean {
  return err instanceof ConnectError && err.code === Code.Unknown && err.cause instanceof TypeError;
}

const unreachableSentence = ({ service }: TransportErrorFacts): string =>
  `${service}에 닿지 못했습니다 — 네트워크 연결을 확인하고 다시 시도하세요`;

/** 서비스 경계의 실패 — `message` 는 사용자에게 보일 문장, 코드·원문은 진단용 */
export class ServiceCallError extends Error {
  constructor(
    message: string,
    readonly code: Code,
    /** 서버가 보낸 원문 detail — 화면에 두지 않는다 */
    readonly detail: string,
    readonly cause: ConnectError,
  ) {
    super(message);
    this.name = 'ServiceCallError';
  }
}

/** 전송 오류 하나를 그 서비스의 말로 — 경계 밖에서 부르지 않는다(경계는 {@link createTranslatingClient} 로 클라이언트를 짓는다) */
export function describeTransportError(err: ConnectError, voice: ServiceVoice, action: string): string {
  const detail = err.rawMessage;
  const facts: TransportErrorFacts = { service: voice.service, action, detail, firstLine: detail.split('\n')[0]?.trim() ?? '' };
  if (isUnreachable(err)) return unreachableSentence(facts);
  const sentence = voice.meanings?.[err.code] ?? COMMON_SENTENCES[err.code];
  return sentence ? sentence(facts) : otherSentence(err.code, facts);
}

/**
 * 경계의 클라이언트 — 요청이 던지는 `ConnectError` 를 {@link ServiceCallError} 로 바꿔 던진다. 스트림 응답은 받는 도중의 실패도 같은
 * 말로 바꾼다. 그 밖의 오류(ConnectError 가 아닌 것)는 그대로 지나간다. 요청 꼴은 단항·서버 스트림만 — 다른 꼴의 메서드가 생기면
 * 짓는 자리에서 실패한다(번역 없이 나가지 않게).
 */
export function createTranslatingClient<S extends ServiceType>(serviceType: S, transport: Transport, voice: ServiceVoice<S>): PromiseClient<S> {
  const client = createPromiseClient(serviceType, transport) as unknown as Record<string, (...args: unknown[]) => unknown>;
  const translated: Record<string, (...args: unknown[]) => unknown> = {};
  for (const [key, method] of Object.entries(serviceType.methods)) {
    const action = (voice.actions as Record<string, string>)[key];
    const translate = (err: unknown): unknown =>
      err instanceof ConnectError ? new ServiceCallError(describeTransportError(err, voice as ServiceVoice, action), err.code, err.rawMessage, err) : err;
    if (method.kind === MethodKind.Unary) {
      translated[key] = (...args) => (client[key](...args) as Promise<unknown>).catch((err: unknown) => { throw translate(err); });
    } else if (method.kind === MethodKind.ServerStreaming) {
      translated[key] = (...args) => (async function* () {
        try {
          yield* client[key](...args) as AsyncIterable<unknown>;
        } catch (err) {
          throw translate(err);
        }
      })();
    } else {
      throw new Error(`${serviceType.typeName}.${method.name}: 번역하는 요청 꼴은 단항·서버 스트림뿐입니다 (${MethodKind[method.kind]})`);
    }
  }
  return translated as unknown as PromiseClient<S>;
}
